import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brandsApi, productGroupsApi } from "../../api/endpoints";
import type { BrandCreate, BrandUpdate, ProductGroup } from "@endeks/shared";

export type { ProductGroup };

export function useBrands(search?: string) {
  return useQuery({
    queryKey: ["brands", search],
    queryFn: () => brandsApi.list(search).then((r) => r.data),
  });
}

export function useBrand(id: string) {
  return useQuery({
    queryKey: ["brands", id],
    queryFn: () => brandsApi.get(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BrandCreate) => brandsApi.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: BrandUpdate & { id: string }) =>
      brandsApi.update(id, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => brandsApi.delete(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useReorderBrands() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; order: number }[]) =>
      brandsApi.reorderAll(items).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

// Ürün grubu sözlüğü (TR↔EN eşleşmeli tag'ler)
export function useProductGroups() {
  return useQuery<ProductGroup[]>({
    queryKey: ["product-groups"],
    queryFn: () => productGroupsApi.list().then((r) => r.data),
  });
}

export function useCreateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { nameTr: string; nameEn?: string }) =>
      productGroupsApi.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });
}

export function useUpdateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; nameTr?: string; nameEn?: string }) =>
      productGroupsApi.update(id, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });
}

export function useDeleteProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productGroupsApi.delete(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });
}
